import { CSS } from '@dnd-kit/utilities';
import { IGrupos } from '../../interface';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../../interfaces/Global';
import { arrayMove, SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';

interface ISortableItemProps {
    id: string;
    nome: string;
}

interface IDragAndDropsGrupos {
    grupos: IGrupos[];
    setGrupos: React.Dispatch<React.SetStateAction<IGrupos[]>>;
}

function SortableItem({ id, nome }: ISortableItemProps) {
    const {
        listeners,
        transform,
        transition,
        attributes,
        setNodeRef,
        isDragging,
    } = useSortable({ id });

    const isDark = useSelector((state: stateGlobal) => state.global.theme === 'dark');

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 140,
        height: 52,
        padding: '0 18px',
        borderRadius: 12,
        cursor: 'grab',
        userSelect: 'none',
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 0.2,
        whiteSpace: 'nowrap',
        opacity: isDragging ? 0.7 : 1,
        zIndex: isDragging ? 999 : 1,
        border: `1px solid ${isDark ? '#303030' : '#E8E8E8'}`,
        color: isDark ? '#F5F5F5' : '#262626',
        background: isDark ? '#1F1F1F' : '#FFFFFF',
        boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.35)' : '0 1px 3px rgba(0,0,0,0.06)',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {nome}
        </div>
    );
}

export function DragAndDropsGrupos({ grupos, setGrupos }: IDragAndDropsGrupos) {
    const sensors = useSensors(useSensor(PointerSensor));
    const isDark = useSelector((state: stateGlobal) => state.global.theme === 'dark');

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setGrupos((items) => {
            const oldIndex = items.findIndex((i) => i.id === active.id);
            const newIndex = items.findIndex((i) => i.id === over.id);
            const reordered = arrayMove(items, oldIndex, newIndex);

            return reordered.map((item, index) => ({
                ...item,
                ordem: String(index + 1),
            }));
        });
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={grupos.map((i) => i.id)} strategy={horizontalListSortingStrategy}>
                <div
                    style={{
                        gap: 8,
                        padding: 8,
                        display: 'flex',
                        flexWrap: 'wrap',
                        overflowX: 'auto',
                        borderRadius: 14,
                        background: isDark ? '#141414' : '#FAFAFA',
                    }}
                >
                    {grupos.map((item) => (
                        <SortableItem key={item.id} id={item.id} nome={item.nome} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
